/**
 * Riquadro di trasformazione unico per le gabbie selezionate in pre-elaborazione (volantino e
 * variante), che siano una o più. Si aggancia ai rettangoli "zone-rect" del layer in cui è
 * montato: trascinando una gabbia della selezione Konva sposta anche le altre, e tirando una
 * maniglia le ridimensiona insieme. Shift blocca le proporzioni, come in InDesign.
 * Il riquadro non esce dalla pagina [0..pageW] × [0..pageH] e non scende sotto 10 px schermo.
 * Va montato nello stesso Layer dei rettangoli, dopo di essi (resta disegnato sopra).
 * @module flyers/components/ZoneSelectionTransformer
 */

import React, {useEffect, useRef} from "react";
import {Transformer} from "react-konva";

const MIN_BOX_SIZE = 10;

export default function ZoneSelectionTransformer({nodeIds, pageW, pageH})
{
	const trRef = useRef(null);

	// Riaggancio ad ogni render, ma solo se l'elenco dei nodi è davvero cambiato: riassegnare
	// i nodi durante un trascinamento o un ridimensionamento in corso lo interromperebbe. Un
	// confronto sui soli id non basta: al cambio pagina i rettangoli vengono ricreati.
	useEffect(() =>
	{
		const tr = trRef.current;
		const layer = tr?.getLayer();
		if (!tr || !layer) return;
		const wanted = new Set(nodeIds);
		const nodes = layer.find(".zone-rect").filter((node) => wanted.has(node.id()));
		const current = tr.nodes();
		const unchanged = current.length === nodes.length && current.every((node, i) => node === nodes[i]);
		if (unchanged) return;
		tr.nodes(nodes);
		layer.batchDraw();
	});

	// Il riquadro arriva in coordinate assolute (canvas, con zoom e margine): la pagina si
	// riporta nello stesso spazio con la trasformazione del layer. Si blocca il bordo che
	// supera la pagina invece di rifiutare il passo, così il riquadro "si appoggia" al bordo.
	const boundBoxFunc = (oldBox, newBox) =>
	{
		const layer = trRef.current?.getLayer();
		let {x, y, width, height} = newBox;
		if (layer && pageW > 0 && pageH > 0)
		{
			const transform = layer.getAbsoluteTransform();
			const topLeft = transform.point({x: 0, y: 0});
			const bottomRight = transform.point({x: pageW, y: pageH});
			if (x < topLeft.x)
			{
				width -= topLeft.x - x;
				x = topLeft.x;
			}
			if (y < topLeft.y)
			{
				height -= topLeft.y - y;
				y = topLeft.y;
			}
			if (x + width > bottomRight.x) width = bottomRight.x - x;
			if (y + height > bottomRight.y) height = bottomRight.y - y;
		}
		if (width < MIN_BOX_SIZE || height < MIN_BOX_SIZE) return oldBox;
		return {...newBox, x, y, width, height};
	};

	return (
		<Transformer
			ref={trRef}
			rotateEnabled={false}
			keepRatio={false}
			flipEnabled={false}
			ignoreStroke
			boundBoxFunc={boundBoxFunc}
		/>
	);
}
